import React from "react";
import { useIsMobile } from "@/hooks/use-mobile";

const HowItWorks = ({ fullPage = false }: { fullPage?: boolean }) => {
  const isMobile = useIsMobile();

  return (
    <section 
      id="howItWorks" 
      className={`py-20 bg-gray-50 relative ${fullPage ? 'min-h-screen flex items-center' : ''}`}
    >
      {/* Background decorative elements */}
      <div className="absolute top-40 right-0 w-96 h-96 bg-[#1211ca]/5 rounded-full blur-3xl -z-10" />
      
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            How <span className="text-gradient">Savyi</span> Works
          </h2>
          <p className="text-gray-600 text-lg">
            From chaos to clarity in four simple steps
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div className="order-2 md:order-1">
            <div className="space-y-12">
              <Step
                number="01"
                title="Connect or Scan Your Financial Life"
                description="Connect accounts or scan your messy life (receipts, notes, threats). No need to be organized - we'll sort through the chaos."
              />
              
              <Step
                number="02"
                title="AI Analyzes Context & Urgency"
                description="AI analyzes context (deadlines, penalties, your rants) and prioritizes what needs your attention first."
              />
              
              <Step
                number="03"
                title="Crush Goals with Smart Gamification"
                description="Turn financial tasks into rewarding challenges with urgency-aware gamification that understands your real priorities."
              />
              
              <Step
                number="04"
                title="Celebrate Wins, Not Guilt"
                description="Track your progress with motivating visualizations and celebrate wins with confetti, not guilt."
              />
            </div>
          </div>
          
          {/* Device Mockup Section */}
          <div className="order-1 md:order-2 relative">
            <div className="relative mx-auto max-w-sm">
              {/* Main Device showing Financial Health */}
              <div className="bg-white rounded-[2rem] border-[12px] border-[#000000] shadow-2xl overflow-hidden">
                <div className="relative">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-6 bg-[#000000] rounded-b-xl z-10"></div>
                  
                  {/* Screen Content */}
                  <div className="h-[540px] bg-gradient-to-br from-[#1211ca]/90 to-[#0072ff]/90 p-6 flex flex-col items-center justify-center text-white">
                    <div className="text-center mb-8">
                      <h3 className="text-2xl font-bold mb-2">Dream Fund Garden</h3>
                      <p className="text-white/80 text-sm">Your goals are growing!</p>
                    </div>
                    
                    {/* Visual garden representation */}
                    <div className="w-full aspect-square rounded-2xl bg-white/10 backdrop-blur-sm p-4 relative">
                      {/* Various plant elements */}
                      <div className="absolute bottom-4 left-1/4 w-20 h-32">
                        <div className="absolute bottom-0 w-full">
                          <div className="w-4 h-20 mx-auto bg-gradient-to-t from-green-700 to-green-500 rounded-full"></div>
                          <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-16 h-16 bg-green-400 rounded-full"></div>
                          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-14 h-14 bg-green-300 rounded-full"></div>
                          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-10 bg-green-200 rounded-full flex items-center justify-center">
                            <span className="text-xs font-bold text-[#1211ca]">£450</span>
                          </div>
                        </div>
                        <div className="absolute bottom-2 w-full text-center">
                          <span className="text-xs bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full">Vacation</span>
                        </div>
                      </div>
                      
                      <div className="absolute bottom-4 right-1/4 w-16 h-24">
                        <div className="absolute bottom-0 w-full">
                          <div className="w-3 h-12 mx-auto bg-gradient-to-t from-green-700 to-green-500 rounded-full"></div>
                          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-12 h-12 bg-green-400 rounded-full"></div>
                          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-8 bg-green-300 rounded-full flex items-center justify-center">
                            <span className="text-xs font-bold text-[#1211ca]">£200</span>
                          </div>
                        </div>
                        <div className="absolute bottom-2 w-full text-center">
                          <span className="text-xs bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full">Tech</span>
                        </div>
                      </div>
                      
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 w-24 h-40">
                        <div className="absolute bottom-0 w-full">
                          <div className="w-5 h-28 mx-auto bg-gradient-to-t from-green-800 to-green-600 rounded-full"></div>
                          <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-20 h-20 bg-green-500 rounded-full"></div>
                          <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-16 h-16 bg-green-400 rounded-full"></div>
                          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-12 h-12 bg-green-300 rounded-full"></div>
                          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-10 bg-green-200 rounded-full flex items-center justify-center">
                            <span className="text-xs font-bold text-[#1211ca]">£1250</span>
                          </div>
                        </div>
                        <div className="absolute bottom-2 w-full text-center">
                          <span className="text-xs bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full">New Car</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-8 w-full">
                      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                        <p className="text-sm font-medium mb-2">Your garden is thriving! 🌱</p>
                        <p className="text-xs text-white/80">
                          You've paid all your bills on time this month. Your emergency fund could use some love though.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating notification */}
              <div className="absolute -top-6 -right-4 bg-white rounded-lg shadow-lg p-3 z-20 animate-float">
                <div className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-teal-500">
                    <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                  <span className="text-xs font-medium">Bill paid! +50 points</span>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 rotate-6 z-20 animate-float" style={{ animationDelay: "1.5s" }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#1211ca]/10 flex items-center justify-center text-[#1211ca]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20.42 4.58a5.4 5.4 0 0 0-7.65 0l-.77.78-.77-.78a5.4 5.4 0 0 0-7.65 0C1.46 6.7 1.33 10.28 4 13l8 8 8-8c2.67-2.72 2.54-6.3.42-8.42z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs font-bold">Streak: 7 days!</p>
                    <p className="text-xs text-gray-500">Keep it growing</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

interface StepProps {
  number: string;
  title: string;
  description: string;
}

const Step = ({ number, title, description }: StepProps) => {
  return (
    <div className="flex gap-6">
      <div className="flex-shrink-0">
        <div className="w-12 h-12 bg-[#1211ca]/10 rounded-full flex items-center justify-center text-[#1211ca] font-bold">
          {number}
        </div>
      </div>
      <div>
        <h3 className="text-xl font-bold mb-2">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </div>
    </div>
  );
};

export default HowItWorks;
